import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPinIcon, GlobeIcon, CheckCircleIcon, StarIcon, PenToolIcon, CalendarIcon, HeartIcon, MailIcon, ExternalLinkIcon, ShareIcon, StoreIcon } from 'lucide-react';
import { getBookstoreBySlug, getBooksForBookstore } from '../data/bookstoresData';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { usePlatform } from '../context/PlatformContext';
const BookstoreProfile = () => {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const dk = useDarkClasses();
  const {
    isAfterTide
  } = usePlatform();
  const bookstore = slug ? getBookstoreBySlug(slug) : undefined;
  const linkedBooks = bookstore ? getBooksForBookstore(bookstore.id) : [];
  if (!bookstore) {
    return <div className={`min-h-screen flex items-center justify-center ${dk.page}`} data-id="element-8580" data-mp="38sbsvdkzrhis">
        <div className="text-center" data-id="element-8581" data-mp="3dren02zfo3bz">
          <StoreIcon className={`h-16 w-16 mx-auto mb-4 ${dk.textMuted}`} data-id="element-8582" data-mp="20syeqcl8tmsn" />
          <h2 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-8583" data-mp="1w1opve7iqnht">
            Bookstore Not Found
          </h2>
          <p className={`mb-6 ${dk.textSecondary}`} data-id="element-8584" data-mp="1r23q51kiyb41">
            The bookstore you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/bookstores" className={`px-6 py-2 rounded-lg font-medium ${isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e]' : 'bg-indigo-600 text-white'}`} data-id="element-8585" data-mp="1n2l2hy1bjlxi">
            Back to Directory
          </Link>
        </div>
      </div>;
  }
  const formatType = (type: string) => {
    return type.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const verifiedColor = isAfterTide ? 'text-[#e2b04a]' : 'text-blue-500';
  const accentColor = isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e] hover:bg-[#d1a03a]' : 'bg-indigo-600 text-white hover:bg-indigo-700';
  const getAvailabilityBadge = (type: string) => {
    switch (type) {
      case 'stocked':
        return {
          label: 'In Stock',
          color: 'bg-green-500'
        };
      case 'online-listing':
        return {
          label: 'Online',
          color: 'bg-blue-500'
        };
      case 'signed-copy':
        return {
          label: 'Signed',
          color: 'bg-purple-500'
        };
      case 'pre-order':
        return {
          label: 'Pre-order',
          color: 'bg-amber-500'
        };
      default:
        return {
          label: 'Available',
          color: 'bg-gray-500'
        };
    }
  };
  return <div className={`min-h-screen pb-16 ${dk.page}`} data-id="element-8586" data-mp="36fazlvks3p2">
      {/* Banner */}
      <div className="w-full h-48 md:h-64 lg:h-80 relative bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-800 dark:to-gray-900" data-id="element-8587" data-mp="84yznu1jztl4">
        {bookstore.bannerImage && <img src={bookstore.bannerImage} alt="Banner" className="w-full h-full object-cover" data-id="element-8588" data-mp="3v952sf931bk0" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-8589" data-mp="3bn3idy7is1do"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 md:-mt-24 relative z-10" data-id="element-8590" data-mp="36pv3c6ho4dgn">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-8591" data-mp="3klf87y0kav52">
          {/* Main Content */}
          <div className="flex-1" data-id="element-8592" data-mp="3gkmxt1fyek77">
            {/* Header Info Card */}
            <div className={`rounded-2xl shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-8593" data-mp="3uko1eonls5qd">
              <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center" data-id="element-8594" data-mp="3pn4j9dnsb8t5">
                <img src={bookstore.logo} alt={bookstore.name} className={`w-24 h-24 sm:w-32 sm:h-32 rounded-xl object-cover border-4 shadow-sm ${dk.isDark ? 'border-gray-800' : 'border-white'}`} data-id="element-8595" data-mp="86mqo9nme71u" />
                <div className="flex-1" data-id="element-8596" data-mp="h4gjvbq38kks">
                  <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-8597" data-mp="c8hr7bxq8gek">
                    <h1 className={`text-2xl sm:text-3xl font-bold ${dk.textHeading}`} data-id="element-8598" data-mp="26o6o2wdrgh4v">
                      {bookstore.name}
                    </h1>
                    {bookstore.verified && <CheckCircleIcon className={`h-6 w-6 ${verifiedColor}`} title="Verified Store" data-id="element-8599" data-mp="2gf19xzn3heps" />}
                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-8600" data-mp="1nqdxmtkbpzod">
                      {formatType(bookstore.type)}
                    </span>
                  </div>

                  <div className={`flex flex-wrap items-center gap-4 text-sm mb-2 ${dk.textSecondary}`} data-id="element-8601" data-mp="21q53g70os9ra">
                    <div className="flex items-center" data-id="element-8602" data-mp="3d3hd4yj4nmld">
                      <MapPinIcon className="h-4 w-4 mr-1" data-id="element-8603" data-mp="3d63m92lo2a5p" />
                      {bookstore.city ? `${bookstore.city}, ` : ''}
                      {bookstore.country}
                    </div>
                    <div className="flex items-center text-yellow-500" data-id="element-8604" data-mp="3i9mg18b0md2q">
                      <StarIcon className="h-4 w-4 fill-current mr-1" data-id="element-8605" data-mp="3dgteetaufkuv" />
                      <span className="font-bold" data-id="element-8606" data-mp="39h2he7uq48hw">{bookstore.rating}</span>
                      <span className={`ml-1 ${dk.textMuted}`} data-id="element-8607" data-mp="34hg2ltz8yk6z">
                        ({bookstore.reviewCount} reviews)
                      </span>
                    </div>
                  </div>

                  {bookstore.address && <p className={`text-sm mb-4 ${dk.textMuted}`} data-id="element-8608" data-mp="2ukbdbla3u8ly">
                      {bookstore.address}
                    </p>}

                  <div className="flex flex-wrap gap-2" data-id="element-8609" data-mp="srwyww6rrmaa">
                    {bookstore.specializations.map((spec, idx) => <span key={idx} className={`text-xs px-3 py-1 rounded-md font-medium ${dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`} data-id="element-8610" data-mp="oq1zjx8efiz5" data-mpi={idx}>
                        {spec}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Description & Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-8611" data-mp="2d7v3mllptk3o">
              <div className="md:col-span-2" data-id="element-8612" data-mp="3101tr30ukuun">
                <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-8613" data-mp="35ymxhcfb5wnu">
                  About the Store
                </h2>
                <p className={`whitespace-pre-line leading-relaxed ${dk.textSecondary}`} data-id="element-8614" data-mp="2rxsk0fdel5ib">
                  {bookstore.description}
                </p>
              </div>

              <div data-id="element-8615" data-mp="1ginivfsx7f0t">
                <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-8616" data-mp="1gidnfcp3wfnq">
                  Features
                </h2>
                <ul className="space-y-3" data-id="element-8617" data-mp="1lgf8g363ep1u">
                  {bookstore.features.signedCopies && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-8618" data-mp="1d3lub8nr4c4p">
                      <PenToolIcon className={`h-5 w-5 mr-3 ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500'}`} data-id="element-8619" data-mp="18zwd549eowtp" />
                      Carries Signed Copies
                    </li>}
                  {bookstore.features.hostsEvents && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-8620" data-mp="u96y1qstqxkk">
                      <CalendarIcon className={`h-5 w-5 mr-3 ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500'}`} data-id="element-8621" data-mp="pdrkkzyqb3oo" />
                      Hosts Author Events
                    </li>}
                  {bookstore.features.indieFocused && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-8622" data-mp="bixkxlvi1le3">
                      <HeartIcon className={`h-5 w-5 mr-3 ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500'}`} data-id="element-8623" data-mp="6knlgz2qrymo" />
                      Indie-Focused Selection
                    </li>}
                  {bookstore.features.internationalShipping && <li className={`flex items-center ${dk.textPrimary}`} data-id="element-8624" data-mp="3r4d0tzbzzxud">
                      <GlobeIcon className={`h-5 w-5 mr-3 ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500'}`} data-id="element-8625" data-mp="o8w79j1knd" />
                      International Shipping
                    </li>}
                </ul>
              </div>
            </div>

            {/* Linked Books Section */}
            <div data-id="element-8626" data-mp="dtdxmjgfx3o6">
              <div className="flex items-center justify-between mb-6" data-id="element-8627" data-mp="ipuy8d4ttai5">
                <h2 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-8628" data-mp="is79y8jzqomr">
                  Books Available Here
                </h2>
                <span className={`text-sm font-medium ${dk.textMuted}`} data-id="element-8629" data-mp="rqj94yyxauqt">
                  {linkedBooks.length} titles
                </span>
              </div>

              {linkedBooks.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6" data-id="element-8630" data-mp="286qg20pxdc66">
                  {linkedBooks.map((item, idx) => {
                const isSigned = item.linkInfo.availabilityType === 'signed-copy';
                const badge = getAvailabilityBadge(item.linkInfo.availabilityType);
                return <div key={idx} className="relative group" data-id="element-8631" data-mp="2qysst0zg48b5" data-mpi={idx}>
                        <UnifiedBookCard book={item.book} isSigned={isSigned} data-id="element-8632" data-mp="2m6ri3jhxepn8" />
                        {!isSigned && <div className={`absolute bottom-2 right-2 z-30 ${badge.color} text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-sm`} data-id="element-8633" data-mp="2h8pviolak870">
                            {badge.label}
                          </div>}
                      </div>;
              })}
                </div> : <div className={`text-center py-12 rounded-xl border ${dk.card}`} data-id="element-8634" data-mp="6rnjzgor3mzc">
                  <BookIcon className={`h-10 w-10 mx-auto mb-3 ${dk.textMuted}`} data-id="element-8635" data-mp="loxwmtd4qywn" />
                  <p className={dk.textSecondary} data-id="element-8636" data-mp="gqw9nxcl2ffw">
                    No books have been linked to this store yet.
                  </p>
                </div>}
            </div>
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-6" data-id="element-8637" data-mp="14edwrfkwkpcj">
            {/* Action Card */}
            <div className={`rounded-xl shadow-sm p-6 border ${dk.card}`} data-id="element-8638" data-mp="zerlhbomxj6d">
              <a href={bookstore.websiteUrl} target="_blank" rel="noopener noreferrer" className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors mb-3 ${accentColor}`} data-id="element-8639" data-mp="1dej5pu5qvvst">
                <ExternalLinkIcon className="h-4 w-4 mr-2" data-id="element-8640" data-mp="1x0m0hz4uhtpy" />
                Visit Website
              </a>

              <button className={`w-full flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors border ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`} data-id="element-8641" data-mp="1j13586x4ucv2">
                <ShareIcon className="h-4 w-4 mr-2" data-id="element-8642" data-mp="1996yk7gzdef8" />
                Share Profile
              </button>
            </div>

            {/* Details Card */}
            <div className={`rounded-xl shadow-sm p-6 border ${dk.card}`} data-id="element-8643" data-mp="10bmqaekpahv4">
              <h3 className={`font-bold mb-4 ${dk.textHeading}`} data-id="element-8644" data-mp="14b5f3lfn7d38">
                Store Details
              </h3>

              <div className="space-y-4" data-id="element-8645" data-mp="qdgrw6y3rtv0">
                {/* Address Section */}
                <div data-id="element-8646" data-mp="vbiciwb6g18l">
                  <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-1`} data-id="element-8647" data-mp="v9xj5a0lmocb">
                    Address
                  </span>
                  {bookstore.type === 'online-only' ? <p className={`text-sm ${dk.textSecondary}`} data-id="element-8648" data-mp="2o7ulf5fqp3lh">
                      Online Only — No Physical Location
                    </p> : bookstore.address ? <p className={`text-sm ${dk.textPrimary}`} data-id="element-8649" data-mp="3fz0gv8kimd4f">
                      {bookstore.address}
                    </p> : <p className={`text-sm ${dk.textSecondary}`} data-id="element-8650" data-mp="3by6rsbvc6s9y">
                      {bookstore.city ? `${bookstore.city}, ` : ''}
                      {bookstore.country}
                    </p>}
                </div>

                {bookstore.contactEmail && <div data-id="element-8651" data-mp="1vk8835gpmklf">
                    <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-1`} data-id="element-8652" data-mp="1vhcaji8rxxxi">
                      Contact
                    </span>
                    <a href={`mailto:${bookstore.contactEmail}`} className={`flex items-center text-sm hover:underline ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-600'}`} data-id="element-8653" data-mp="1gekl8f8ww8c0">
                      <MailIcon className="h-4 w-4 mr-2" data-id="element-8654" data-mp="1v5jpje2psaps" />
                      {bookstore.contactEmail}
                    </a>
                  </div>}

                <div data-id="element-8655" data-mp="245zruk7q1bt2">
                  <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-1`} data-id="element-8656" data-mp="2456v8c2h82on">
                    Ships To
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-8657" data-mp="2e4fmuy9vd57u">
                    {bookstore.shippingRegions.map((region, idx) => <span key={idx} className={`text-xs px-2 py-1 rounded ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-8658" data-mp="esmmcrnvhhqd" data-mpi={idx}>
                        {region}
                      </span>)}
                  </div>
                </div>

                {bookstore.socialLinks && bookstore.socialLinks.length > 0 && <div data-id="element-8659" data-mp="2e8u1ooswf00a">
                    <span className={`text-xs uppercase font-bold tracking-wider ${dk.textMuted} block mb-2`} data-id="element-8660" data-mp="2e8byt9bqdnje">
                      Social Media
                    </span>
                    <div className="flex flex-col gap-2" data-id="element-8661" data-mp="1mip9w52t7ap1">
                      {bookstore.socialLinks.map((social, idx) => <a key={idx} href={social.url} target="_blank" rel="noopener noreferrer" className={`text-sm hover:underline ${isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-600'}`} data-id="element-8662" data-mp="20adja0lobl2b" data-mpi={idx}>
                          {social.platform}
                        </a>)}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Claim Profile */}
            {!bookstore.claimed && <div className={`rounded-xl p-5 border border-dashed ${dk.isDark ? 'border-gray-600 bg-gray-800/50' : 'border-gray-300 bg-gray-50'}`} data-id="element-8663" data-mp="35eitv50ya6d5">
                <h4 className={`font-bold text-sm mb-2 ${dk.textPrimary}`} data-id="element-8664" data-mp="20epk95kvelr">
                  Is this your store?
                </h4>
                <p className={`text-xs mb-3 ${dk.textSecondary}`} data-id="element-8665" data-mp="3o6tpcdt9z218">
                  Claim this profile to manage your information, verify your
                  store, and approve book links.
                </p>
                <button className={`text-xs font-medium px-3 py-1.5 rounded border transition-colors ${dk.isDark ? 'border-gray-500 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-white text-gray-700'}`} data-id="element-8666" data-mp="2cr55vwf8cl56">
                  Claim Profile
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default BookstoreProfile;